import { FormGroup, Input, Progress, Spinner } from '@agro-club/agroclub-shared'
import env from 'env'
import { CurrencySign, Hedges } from 'modules/domain/export/types'
import { FormikFieldsType } from 'modules/domain/types'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { DatePicker } from 'views/components/DatePicker/DatePicker'
import { FormInput } from 'views/components/form/FormInput'
import { FormCurrencySelect } from 'views/components/FormCurrencySelect/FormCurrencySelect'
import { useFormNetRate } from './useFormNetRate'

export type HedgingFields = Nullable<Omit<Hedges, 'id' | 'net_rate'>>

const currency = env.REACT_APP_CURRENCY_SYMBOL

const NetRateSpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
`

const NetRateSpinner: FC<{ progress?: Progress }> = ({ progress }) =>
  progress === Progress.WORK ? (
    <NetRateSpinnerWrapper>
      <Spinner size="small" />
    </NetRateSpinnerWrapper>
  ) : undefined

type Props = {
  formik: FormikFieldsType
  initNetRate?: number
}

export const CommonOptionFields: FC<Props> = ({ formik, initNetRate }) => {
  const { t } = useTranslation('export')
  const { netRateProgress, netRate } = useFormNetRate(formik, initNetRate || 0)

  const formCurrencyId = formik.values.currency

  return (
    <>
      <FormCurrencySelect label={t('currency')} formik={formik} field="currency" />
      <FormInput formik={formik} field="bank_deal_number" label={t('dealNumberFromBank')} type="number" />
      <FormInput
        formik={formik}
        field="option_volume"
        label={`${t('optionSize')}, ${CurrencySign[formCurrencyId]}`}
        type="number"
      />
      <FormInput
        formik={formik}
        field="strike"
        label={`${t('strike')}, ${t('currencyPerCurrency', { currency, perCurrency: CurrencySign[formCurrencyId] })} `}
        type="number"
      />
      <FormGroup label={t('expirationDate')} error={formik.errors.expiration_date}>
        <DatePicker
          fullWidth
          value={formik.values.expiration_date}
          onChange={(data) => formik.setFieldValue('expiration_date', data['date'])}
          error={!!formik.errors.expiration_date}
        />
      </FormGroup>
      <FormGroup label={t('calculationDate')} error={formik.errors.settlement_date}>
        <DatePicker
          fullWidth
          value={formik.values.settlement_date}
          onChange={(data) => formik.setFieldValue('settlement_date', data['date'])}
          error={!!formik.errors.settlement_date}
        />
      </FormGroup>
      <FormInput
        formik={formik}
        field="option_bonus"
        label={`${t('amountOptionPremium')}, ${currency}`}
        type="number"
      />
      <FormGroup
        label={`${t('netRate')}, ${t('currencyPerCurrency', { currency, perCurrency: CurrencySign[formCurrencyId] })} `}
        afterControlComponent={<NetRateSpinner progress={netRateProgress} />}
      >
        <Input value={netRate?.toString()} disabled />
      </FormGroup>
      <FormGroup label={t('plannedDateReceiptMoney')} error={formik.errors.signed_date}>
        <DatePicker
          fullWidth
          value={formik.values.receipt_planned_date}
          onChange={(data) => formik.setFieldValue('receipt_planned_date', data['date'])}
          error={!!formik.errors.receipt_planned_date}
        />
      </FormGroup>
    </>
  )
}
